import { h, options, render, VNode } from 'preact'
import { PreviewId, WidgetLayout, WidgetSettings } from '@gsc/types'
import { IWidget, IWidgetRenderer, IWidgetRendererOptions, IWidgetRuntime } from '../interfaces'
import { domReady } from '../shared/dom'
import { findInlineWidgetPlaceholder } from '../shared/widget'
import { makePreviewNode } from './preview'
import { AppDispatch, configureStore } from './store/store'
import { updateGlobalSettings } from './store/globalSettings'
import { createOrUpdateWidget, setVisibility } from './store/widgets'
import { setTags } from './store/templateTags'
import { createWidgetInstance } from './instance'
import { App } from './App'

class Widget implements IWidget {
  private readonly _id: number
  public readonly shown: boolean
  public readonly date = new Date()

  constructor(settings: Readonly<WidgetSettings>, private _dispatch: AppDispatch) {
    const widget = createWidgetInstance(settings)
    this._id = widget.id

    if (!PREVIEW && widget.layout === WidgetLayout.Inline && !findInlineWidgetPlaceholder(widget.id)) {
      this.shown = false
      return
    }

    this.shown = true
    this._dispatch(createOrUpdateWidget(widget))
  }

  public close() {
    this._dispatch(setVisibility(this._id, false))
  }
}

const oldVNodeHook = options.vnode

export class WidgetRenderer implements IWidgetRenderer {
  private _isRendered = false
  private _options: IWidgetRendererOptions
  private _store = configureStore(this._runtime)

  constructor(private _runtime: IWidgetRuntime, opts: IWidgetRendererOptions) {
    this.updateOptions(opts)

    if (PREVIEW) {
      options.vnode = (node: VNode<{ previewId?: PreviewId }>) => {
        if (node.props?.previewId) {
          makePreviewNode(node, this._options)
        }
        oldVNodeHook(node)
      }
    }
  }

  public isRendered(): boolean {
    return this._isRendered
  }

  public render(initialContext: { [key: string]: any } = {}): void {
    this._isRendered = true
    this.updateContext(initialContext)

    domReady(() => {
      // see https://github.com/preactjs/preact/issues/2264
      const root = document.createDocumentFragment()
      document.body.appendChild(root)
      render(h(App, { store: this._store }), root)
    })
  }

  public async createWidget(settings: Readonly<WidgetSettings>): Promise<IWidget> {
    return new Widget(settings, this._store.dispatch)
  }

  public updateContext(ctx: { [key: string]: any }): void {
    this._store.dispatch(setTags(ctx))
  }

  public updateOptions(opts: IWidgetRendererOptions): void {
    this._options = opts
    this._store.dispatch(updateGlobalSettings({ logoUrl: opts.logoUrl }))
  }
}
